import type { MemberCompletionSucceeded } from "./registration-invitation-client";
import type { FinancialHandoffDescriptor } from "./registration-financial-outcome";

/**
 * Module-memory holder for the FR-046 handoff-scoped access token: the
 * short-lived (~30 min) secret bearer JWT the Member completion response
 * returns alongside the non-secret `handoffId`, authorizing ONLY the
 * registration-financial status and receipt-copy endpoints for that single
 * handoff. Pattern precedent: first-credential-proof-holder /
 * first-credential-memory-handoff — React double-buffers fibers, so a
 * secret placed in immutable state or props survives in the `alternate`
 * tree after a state "scrub"; this mutable module-level slot is never part
 * of any fiber's hook state or props, so alternate-fiber retention is
 * structurally impossible. Nothing here ever touches the URL, storage,
 * history, caches, or logs.
 *
 * Scoping: reads and clears are gated on the owning `handoffId` (the same
 * role the opaque owner symbol plays in first-credential-proof-holder), so
 * a stale flow's id can neither read nor clear a newer handoff's token. In
 * addition, every deposit mints an opaque OWNER token (generation): the
 * route-unmount scrub clears by owner, so a stale flow's deferred cleanup
 * can never erase a newer deposit (StrictMode-safe with the page's deferred
 * cleanup pattern). A new deposit replaces the previous one — a page only
 * ever serves one pending handoff. The token is scrubbed on: a 401 from any
 * endpoint (no refresh loop — FR-046 expiry UX), a terminal financial
 * status (no further authenticated call exists), and route unmount.
 */

/** Opaque, non-secret ownership token minted per deposit. */
export type RegistrationFinancialTokenOwner = symbol;

interface HeldRegistrationFinancialToken {
  handoffId: string;
  accessToken: string;
  receivedAt: number;
}

let held: HeldRegistrationFinancialToken | null = null;
let currentOwner: RegistrationFinancialTokenOwner | null = null;

/**
 * Parks the token for its handoff, replacing any previously held one, and
 * mints the deposit's owner token (the capability for the owner-scoped
 * scrub — a stale owner's clear is a no-op).
 */
export function depositRegistrationFinancialToken(
  deposit: HeldRegistrationFinancialToken,
): RegistrationFinancialTokenOwner {
  const owner: RegistrationFinancialTokenOwner = Symbol("registration-financial-token-owner");
  held = { ...deposit };
  currentOwner = owner;
  return owner;
}

/** Non-consuming read, gated on the owning handoffId. */
export function readRegistrationFinancialToken(handoffId: string): string | null {
  return held !== null && held.handoffId === handoffId ? held.accessToken : null;
}

/** Non-secret presence probe so rendering can gate WITHOUT touching the token. */
export function hasRegistrationFinancialToken(handoffId: string): boolean {
  return held !== null && held.handoffId === handoffId;
}

/** Synchronous handoff-scoped scrub: another handoff's clear is a no-op. */
export function clearRegistrationFinancialToken(handoffId: string): void {
  if (held !== null && held.handoffId === handoffId) {
    held = null;
    currentOwner = null;
  }
}

/**
 * Owner-scoped scrub for route-unmount cleanup: only the CURRENT deposit's
 * owner clears; a stale owner (replaced deposit, already-scrubbed token)
 * is a no-op, so a deferred cleanup can never erase a newer deposit.
 */
export function clearRegistrationFinancialTokenByOwner(
  owner: RegistrationFinancialTokenOwner | null,
): void {
  if (owner !== null && owner === currentOwner) {
    held = null;
    currentOwner = null;
  }
}

/**
 * FR-046 completion boundary: accepts the completion response's additive
 * `financialHandoff`, deposits the secret access token into this holder
 * (only when the server sent BOTH the handoffId and the token), and returns
 * a FRESH descriptor carrying only non-secret fields for React state — the
 * raw response object (which may carry the token as a surplus runtime
 * property) must never be stored — plus the deposit's owner token (null
 * when nothing was deposited) for the page's route-unmount scrub.
 */
export function acceptCompletionFinancialHandoff(
  financialHandoff: MemberCompletionSucceeded["financialHandoff"],
): {
  descriptor: FinancialHandoffDescriptor;
  tokenOwner: RegistrationFinancialTokenOwner | null;
} {
  // Additive fields arrive from the network: guard shape, never trust type.
  const handoffId =
    typeof financialHandoff.handoffId === "string" && financialHandoff.handoffId.length > 0
      ? financialHandoff.handoffId
      : undefined;
  const accessToken =
    typeof financialHandoff.accessToken === "string" && financialHandoff.accessToken.length > 0
      ? financialHandoff.accessToken
      : undefined;

  let tokenOwner: RegistrationFinancialTokenOwner | null = null;
  if (handoffId !== undefined && accessToken !== undefined) {
    tokenOwner = depositRegistrationFinancialToken({
      handoffId,
      accessToken,
      receivedAt: Date.now(),
    });
  }

  return {
    descriptor: {
      status: "pending_owner_processing",
      correlationId: financialHandoff.correlationId,
      ...(handoffId !== undefined && { handoffId }),
    },
    tokenOwner,
  };
}

/** Test-only unscoped peek so suites can assert holder contents. */
export function peekRegistrationFinancialTokenForTests(): HeldRegistrationFinancialToken | null {
  return held;
}

/** Test-only unscoped reset for suite setup/teardown isolation. */
export function resetRegistrationFinancialTokenHolderForTests(): void {
  held = null;
  currentOwner = null;
}
